import { useState, useMemo, useRef } from 'preact/hooks'
import { addClass, removeClass } from '@/components/helpers/dom'
import useDetectBreakpoint from 'hooks/useDetectBreakpoint'
import ProductCardBadges from 'snippets/product-card/product-card-badges'
import ProductCardSwatches from 'snippets/product-card/product-card-swatches'
import ProductCardVendor from 'snippets/product-card/product-card-vendor'
import ProductCardPrice from 'snippets/product-card/product-card-price'
import ProductCardCondition from 'snippets/product-card/product-card-condition'
import ProductCardButton from 'snippets/product-card/product-card-button'

const selectors = {
  COLOR_OPTIONS: ['color', 'colors', 'colour', 'colours'],
  ACTIVE_CLASS: 'active'
}

function ProductCard({ collection, product, className = '' }) {
  const imageRef = useRef(null)
  const imageSecondRef = useRef(null)
  const { defaultImage } = window.GM_STATE.shopify
  const { images, variants, options_with_values: options } = product
  const { isMobile } = useDetectBreakpoint()

  const [currentVariant, setCurrentVariant] = useState(variants[0])
  const hasSaleTag = product?.tags?.includes(GM_STATE.product.saleTag) || false

  const url = collection
    ? `/collections/${collection}/products/${product.handle}?variant=${currentVariant.id}`
    : `/products/${product.handle}?variant=${currentVariant.id}`
  const type =
    variants.length === 1 || (options?.length === 1 && selectors.COLOR_OPTIONS.includes(options[0].name.toLowerCase()))
      ? 'add'
      : 'view'
  const position = options.findIndex((option) => selectors.COLOR_OPTIONS.includes(option.name.toLowerCase()))

  const image = useMemo(() => {
    const featuredImage = images.find((image) => image.alt === currentVariant.options[position])

    return featuredImage ? featuredImage.src : images[0]?.src
  }, [currentVariant])

  const secondImage = useMemo(() => {
    const featuredImage = images.filter((image) => image.alt === currentVariant.options[position])

    return featuredImage ? featuredImage[1]?.src : null
  }, [currentVariant])

  const handleMouseEnter = () => {
    if (!secondImage || !imageSecondRef.current) return

    removeClass(selectors.ACTIVE_CLASS, imageRef.current)
    addClass(selectors.ACTIVE_CLASS, imageSecondRef.current)
  }

  const handleMouseLeave = () => {
    if (!secondImage || !imageSecondRef.current) return

    removeClass(selectors.ACTIVE_CLASS, imageSecondRef.current)
    addClass(selectors.ACTIVE_CLASS, imageRef.current)
  }

  return (
    <div className={`product-card group flex h-full flex-col overflow-hidden rounded-lg bg-white shadow ${className}`}>
      <a
        href={url}
        className="relative block aspect-square overflow-hidden"
        title={product.title}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ProductCardBadges product={product} />
        <img
          ref={imageRef}
          src={image || defaultImage}
          className={`active absolute inset-0 size-full bg-white duration-300 ease-in-out [&.active]:visible [&.active]:z-1 [&.active]:opacity-100 ${secondImage ? 'transition-opacity' : 'group-hover:zoom'}`}
          loading="lazy"
          alt={`${translate.product.image} ${product.title}`}
        />
        {secondImage && (
          <img
            ref={imageSecondRef}
            src={secondImage}
            className="invisible absolute inset-0 z-0 size-full bg-white opacity-0 transition-opacity duration-300 ease-in-out [&.active]:visible [&.active]:z-1 [&.active]:opacity-100"
            loading="lazy"
            alt={`${translate.product.secondImage} ${product.title}`}
          />
        )}
      </a>
      <div className="flex flex-1 flex-col p-3 md:p-4">
        <div className="relative">
          {!isMobile &&
            (position !== -1 ? (
              <ProductCardSwatches
                position={position}
                options={options}
                variants={variants}
                currentVariant={currentVariant}
                setCurrentVariant={setCurrentVariant}
              />
            ) : (
              <div className="h-5 w-full"></div>
            ))}
          <ProductCardVendor vendor={product.vendor} />
          <a
            href={url}
            className="mt-1 line-clamp-2 min-h-11 text-button font-semibold text-grey-900 md:mt-2 md:min-h-12 md:text-base"
            title={product.title}
          >
            {product.title}
          </a>
        </div>
        <div className="mt-2 flex flex-1 flex-col gap-3 md:mt-3">
          <ProductCardPrice type={type} price={product.price} variant={currentVariant} hasSaleTag={hasSaleTag} />
          <ProductCardCondition tags={product.tags} />
        </div>
      </div>
      <ProductCardButton id={product.id} url={url} type={type} variant={currentVariant} />
    </div>
  )
}

export default ProductCard
